import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { Popover, TriggerButton, useContextualMenu } from "widgets";

import { loadPlaybooks } from "@/app-utils/playbook.ts";
import type { InputTableQuerySpec } from "@/graphql.ts";
import type { ITableData } from "@/redux/modules/table-data.ts";
import { getTableQuerySpecWithSelection } from "@/redux/modules/table-data.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, FEATURE_DISPLAY_NAME, PLAYBOOK_CLASS } from "@/settings/settings.ts";
import { ReportPlaybooksMenuDialog } from "@/ui-modules/core/ReportPlaybooksMenuDialog.tsx";

interface IGridReportPlaybooksButtonProps {
  activeTable: ITableData;
}

export const GridReportPlaybooksButton: FC<IGridReportPlaybooksButtonProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const { tableQuerySpec, selectionModel } = props.activeTable;

  const [reportPlaybooksAvailable, setReportPlaybooksAvailable] = useState<boolean>(false);
  useEffect(() => {
    void (async () => {
      const playbooks = await dispatch(loadPlaybooks());
      const reportPlaybooks = playbooks.filter((playbook) => playbook.type.class === PLAYBOOK_CLASS.REPORT);
      setReportPlaybooksAvailable(reportPlaybooks.length > 0);
    })();
  }, [props.activeTable]);

  const buttonDisabledMessage = useMemo(() => {
    if (!reportPlaybooksAvailable) {
      return `There are no Playbooks available for producing a report from there.`;
    }
    if (tableQuerySpec.resultType === ENTITY_TYPE.COORD_LIST && selectionModel.selectedIds.length !== 1) {
      const entityName: string = FEATURE_DISPLAY_NAME[tableQuerySpec.resultType];
      return `Exactly one ${entityName} must be selected to create a report.`;
    }
    return undefined;
  }, [props.activeTable, reportPlaybooksAvailable]);

  const supportedTableQuerySpec = useMemo(() => {
    if (tableQuerySpec.resultType === ENTITY_TYPE.COORD_LIST && selectionModel.selectedIds.length === 1) {
      // we don't want a list of ENTITY_TYPE.COORD_LIST but rather a specification of ENTITY_TYPE.COORDINATE (only one single list is selectable)
      const coordinateListId = selectionModel.selectedIds[0] as number;
      return {
        id: coordinateListId,
        idType: DATATABLE_ID_TYPE.ENTITY,
        queryType: ENTITY_TYPE.COORD_LIST,
        resultType: ENTITY_TYPE.COORDINATE,
        restrictions: {
          refGenomeId: tableQuerySpec.id, // always corresponds to the genome
        },
      } satisfies InputTableQuerySpec;
    }
    return getTableQuerySpecWithSelection(props.activeTable);
  }, [props.activeTable]);

  const triggerRef = useRef(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  return (
    <>
      <TriggerButton
        {...triggerProps}
        ref={triggerRef}
        icon={"summarize"}
        label={"Reports"}
        id={"button-header-report-playbooks"}
        showLabel
        isDisabled={buttonDisabledMessage}
        tooltip={"Select a Playbook to create a report"}
      />
      <Popover state={state} triggerRef={triggerRef} placement={"bottom start"}>
        <ReportPlaybooksMenuDialog {...menuProps} tableQuerySpec={supportedTableQuerySpec} />
      </Popover>
    </>
  );
};
